import * as React from 'react';
import { Box, Button, Stack, ThemeProvider, Typography } from '@mui/material';
import { generateTheme } from '../themes';
import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SchemeBands from './subComponents/SchemeBands';

gsap.registerPlugin(ScrollTrigger);

const buttonSxFor = (style) => {
    const base = {
        borderRadius: '999px',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: { xs: '0.95rem', md: '1rem' },
        px: { xs: 2.5, md: 3.5 },
        py: { xs: 1, md: 1.25 },
        minHeight: 48
    };
    if (style === 'outlined') {
        return {
            ...base,
            color: 'primary.main',
            borderColor: 'primary.main',
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5, borderColor: 'primary.main', backgroundColor: 'rgba(92, 51, 215, 0.08)' }
        };
    }
    if (style === 'outlined-contrast') {
        return {
            ...base,
            color: 'text.primary',
            borderColor: 'text.primary',
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5, borderColor: 'text.primary', backgroundColor: 'rgba(0, 0, 0, 0.04)' }
        };
    }
    return {
        ...base,
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        '&:hover': { backgroundColor: 'primary.dark' }
    };
};

const buttonVariantFor = (style) => (style === 'filled' ? 'contained' : 'outlined');

const MOBILE_TITLE_SIZES = {
    s: { xs: '1.375rem', sm: '1.75rem' },
    m: { xs: '1.75rem', sm: '2.25rem' },
    l: { xs: '2rem', sm: '2.75rem' }
};

const DESKTOP_TITLE_SIZES = {
    s: { md: '2.25rem', lg: '2.5rem' },
    m: { md: '2.75rem', lg: '3.25rem' },
    l: { md: '3.5rem', lg: '4rem' },
    xl: { md: '4.25rem', lg: '5rem' }
};

const titleFontSize = (mobile, desktop) => ({
    ...(MOBILE_TITLE_SIZES[mobile] || MOBILE_TITLE_SIZES.m),
    ...(DESKTOP_TITLE_SIZES[desktop] || DESKTOP_TITLE_SIZES.l)
});

const CtaButton = ({ className, text, url, style }) => (
    <Button
        className={className}
        href={url || '#'}
        variant={buttonVariantFor(style)}
        sx={buttonSxFor(style)}
        disableElevation
    >
        <span dangerouslySetInnerHTML={{ __html: text }} />
        <span aria-hidden="true" style={{ marginLeft: '0.4em' }}>→</span>
    </Button>
);

const SectionIntroSlice = ({ data }) => {
    const {
        colorTheme = 'off-white',
        eyebrow,
        title,
        body,
        titleSizeDesktop = 'l',
        titleSizeMobile = 'm',
        align = 'left',
        primaryCta,
        secondaryCta,
        gradientHeadline = false,
        pinWhileScrolling = true
    } = data;

    const isCentered = align === 'center';

    const theme = generateTheme(colorTheme);
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const targets = gsap.utils.toArray('.si-animate', containerRef.current);
        targets.forEach((el, i) => {
            gsap.fromTo(
                el,
                { opacity: 0, y: 10 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.7,
                    delay: 0.08 * i,
                    ease: 'easeIn',
                    scrollTrigger: {
                        id: 'siAnimate',
                        trigger: el,
                        start: 'top 85%',
                        markers: false
                    }
                }
            );
        });
    }, []);

    useLayoutEffect(() => {
        if (!containerRef.current || !pinWhileScrolling) return;

        const getNavHeight = () => document.querySelector('.ccnav')?.offsetHeight || 64;

        const st = ScrollTrigger.create({
            trigger: containerRef.current,
            start: () => `top ${getNavHeight()}px`,
            end: () => `+=${containerRef.current.offsetHeight}`,
            pin: true,
            pinSpacing: false,
        });

        return () => st.kill();
    }, [pinWhileScrolling]);


    return (
        <ThemeProvider theme={theme}>
            <Box
                ref={containerRef}
                sx={{
                    background: (t) => t.palette.background.default,
                    px: { xs: 2, md: 12 },
                    pt: pinWhileScrolling ? { xs: 3, md: 5 } : { xs: 6, md: 10 },
                    pb: { xs: 6, md: 10 },
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {colorTheme?.startsWith('colorful') && <SchemeBands />}
                <Box sx={{ maxWidth: 1080, mx: 'auto', textAlign: isCentered ? 'center' : 'left', position: 'relative', zIndex: 1 }}>
                    {eyebrow && (
                        <>
                            <Typography
                                className="si-animate"
                                component="span"
                                sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    opacity: 0.7,
                                    mb: 1
                                }}
                                dangerouslySetInnerHTML={{ __html: eyebrow }}
                            />
                            <Box
                                className="si-animate"
                                aria-hidden="true"
                                sx={{
                                    height: '4px',
                                    width: 160,
                                    borderRadius: '2px',
                                    mx: isCentered ? 'auto' : 0,
                                    mb: { xs: 2.5, md: 3 },
                                    background: 'linear-gradient(90deg, rgba(92, 51, 215, 0) 0%, rgb(92, 51, 215) 100%)'
                                }}
                            />
                        </>
                    )}

                    {title && (
                        <Box
                            sx={{
                                width: 'fit-content',
                                maxWidth: 820,
                                mx: isCentered ? 'auto' : 0
                            }}
                        >
                            <Typography
                                className="si-animate"
                                component="h2"
                                lang="sv"
                                sx={{
                                    color: 'text.primary',
                                    fontFamily: 'Kanit, sans-serif',
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    fontSize: titleFontSize(titleSizeMobile, titleSizeDesktop),
                                    lineHeight: 1.05,
                                    letterSpacing: '-0.01em',
                                    overflowWrap: 'break-word',
                                    hyphens: 'auto',
                                    ...(gradientHeadline ? {
                                        background: 'linear-gradient(90deg, rgb(6, 194, 220) 0%, rgb(0, 158, 187) 50%, rgb(92, 51, 215) 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        color: 'transparent'
                                    } : {})
                                }}
                                dangerouslySetInnerHTML={{ __html: title }}
                            />
                        </Box>
                    )}

                    {body && (
                        <Typography
                            className="si-animate"
                            component="p"
                            sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '1rem', md: '1.125rem' },
                                lineHeight: 1.6,
                                maxWidth: 640,
                                mx: isCentered ? 'auto' : 0,
                                mt: { xs: 2.5, md: 3 }
                            }}
                            dangerouslySetInnerHTML={{ __html: body }}
                        />
                    )}

                    {(primaryCta?.text || secondaryCta?.text) && (
                        <Stack
                            className="si-animate"
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={{ xs: 1.5, sm: 2 }}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            justifyContent={isCentered ? { xs: 'center', sm: 'center' } : 'flex-start'}
                            sx={{ mt: { xs: 4, md: 5 } }}
                        >
                            {primaryCta?.text && (
                                <CtaButton
                                    className="si-primary-cta"
                                    text={primaryCta.text}
                                    url={primaryCta.url}
                                    style={primaryCta.style}
                                />
                            )}
                            {secondaryCta?.text && (
                                <CtaButton
                                    className="si-secondary-cta"
                                    text={secondaryCta.text}
                                    url={secondaryCta.url}
                                    style={secondaryCta.style}
                                />
                            )}
                        </Stack>
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default SectionIntroSlice;
